import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import type { DayButton } from 'react-day-picker'
import { fr } from 'react-day-picker/locale'
import { Button } from './button'
import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { useToggle } from '../../hooks/use-toggle'
import { cn } from '../../lib/utils'

export interface DatePreset<TValue extends string = string> {
  value: TValue
  label: string
  /** Returns the date range produced by clicking this preset. `from: null` ⇒ unbounded past ("toujours"). */
  getRange: () => { from: Date | null; to: Date }
}

/** Identifier for the standard preset set exposed via `DEFAULT_PRESETS`. */
export type DateRangePreset =
  | 'yesterday'
  | 'today'
  | '7d'
  | '30d'
  | '3m'
  | '12m'
  | 'mtd'
  | 'ytd'
  | 'all'

/**
 * Range from `start` (start-of-day, or null for unbounded past) through **end of yesterday**
 * (last fully closed day). Presets use this so analytics exclude the incomplete current day.
 * If `start` is after yesterday (e.g. calendar “today”), `from` is clamped to yesterday’s start.
 */
const rangeFromStartThroughYesterday = (
  start: DateTime | null,
): { from: Date | null; to: Date } => {
  const end = DateTime.now().minus({ days: 1 }).endOf('day')
  if (start === null) {
    return { from: null, to: end.toJSDate() }
  }
  let fromDt = start.startOf('day')
  if (fromDt > end) {
    fromDt = end.startOf('day')
  }
  return { from: fromDt.toJSDate(), to: end.toJSDate() }
}

/**
 * Standard preset set — single source of truth for preset ranges.
 * Ranges snap to whole days since the picker has no time-of-day UI.
 */
export const DEFAULT_PRESETS: DatePreset<DateRangePreset>[] = [
  {
    value: 'yesterday',
    label: 'Hier',
    getRange: () => {
      const y = DateTime.now().minus({ days: 1 })
      return { from: y.startOf('day').toJSDate(), to: y.endOf('day').toJSDate() }
    },
  },
  {
    value: 'today',
    label: "Aujourd'hui",
    // Today so far: midnight → now (deliberately not whole-day; the trend
    // comparison in getPreviousPeriodForPreset pairs it with the same slice of yesterday).
    getRange: () => ({
      from: DateTime.now().startOf('day').toJSDate(),
      to: DateTime.now().toJSDate(),
    }),
  },
  {
    value: '7d',
    label: '7 derniers jours',
    getRange: () => rangeFromStartThroughYesterday(DateTime.now().minus({ days: 7 })),
  },
  {
    value: '30d',
    label: '30 derniers jours',
    getRange: () => rangeFromStartThroughYesterday(DateTime.now().minus({ days: 30 })),
  },
  {
    value: '3m',
    label: '3 derniers mois',
    getRange: () => rangeFromStartThroughYesterday(DateTime.now().minus({ months: 3 })),
  },
  {
    value: '12m',
    label: '12 derniers mois',
    getRange: () => rangeFromStartThroughYesterday(DateTime.now().minus({ months: 12 })),
  },
  {
    value: 'mtd',
    label: 'Mois à ce jour',
    getRange: () => rangeFromStartThroughYesterday(DateTime.now().startOf('month')),
  },
  {
    value: 'ytd',
    label: "Depuis le début de l'année",
    getRange: () => rangeFromStartThroughYesterday(DateTime.now().startOf('year')),
  },
  { value: 'all', label: 'Toujours', getRange: () => rangeFromStartThroughYesterday(null) },
]

/** Resolves a standard preset directly to its JS Date range. */
export const getRangeForPreset = (preset: DateRangePreset): { from: Date | null; to: Date } =>
  DEFAULT_PRESETS.find((p) => p.value === preset)!.getRange()

/**
 * True si le jour de `from` précède le jour de `to` moins `months` mois : même règle, au jour
 * près, que le plafond de l'API (`periodExceeds`, `app/utils/requestBounds.ts` du backend), si bien
 * qu'une plage acceptée ici l'est aussi par l'API, presets compris (« 12 derniers mois » des
 * capteurs, qui finit aujourd'hui). `from: null` (« toujours ») dépasse toujours.
 *
 * Les jours sont ceux du navigateur, comme les dates `YYYY-MM-DD` qu'envoient les dashboards :
 * l'API les lit telles quelles. Un écran qui envoie des instants (capteurs) est relu en jours de
 * Paris par l'API ; les deux règles coïncident pour un navigateur à l'heure de Paris.
 */
export const rangeExceedsMaxMonths = (from: Date | null, to: Date, months: number): boolean => {
  if (from === null) return true
  const start = DateTime.fromJSDate(from).startOf('day')
  return start < DateTime.fromJSDate(to).startOf('day').minus({ months })
}

export const rangeExceedsMaxDays = (from: Date | null, to: Date, days: number): boolean => {
  if (from === null) return true
  const start = DateTime.fromJSDate(from).startOf('day')
  const end = DateTime.fromJSDate(to).startOf('day')
  return Math.floor(end.diff(start, 'days').days) + 1 > days
}

export interface DateRangePickerProps<TValue extends string = string> {
  /** Currently applied range. `from: null` ⇒ unbounded past. */
  value: { from: Date | null; to: Date }
  /** Fires only when the user clicks "Appliquer" (not on every staged change). */
  onChange: (range: { from: Date | null; to: Date }, presetValue: TValue | null) => void
  /** Identifier of the active preset, or null for a custom range. */
  activePreset?: TValue | null
  presets: DatePreset<TValue>[]
  className?: string
  disabled?: boolean
  /** Optional placeholder when no range is set; rarely needed since value is required. */
  placeholder?: string
  /** Optional custom trigger content (exposes open state and computed label). */
  triggerContent?: (props: { open: boolean; label: string; disabled: boolean }) => ReactNode
  /**
   * Plafond de la plage sélectionnable, en mois calendaires. Au-delà, « Appliquer » est désactivé
   * et un message l'explique (cf. `rangeExceedsMaxMonths`). Les presets fournis doivent déjà
   * respecter le plafond — le picker ne les filtre pas.
   */
  maxRangeMonths?: number
  maxRangeDays?: number
  minDate?: Date
  maxDate?: Date
  /**
   * Nombre de mois affichés côte à côte. Deux par défaut, ce qui permet de
   * choisir une plage à cheval sur deux mois sans naviguer. Un seul suffit
   * quand la plage autorisée tient dans un mois.
   */
  months?: 1 | 2
}

const formatTriggerLabel = (from: Date | null, to: Date) => {
  const end = DateTime.fromJSDate(to).setLocale('fr')
  const endLabel = end.toFormat('d LLL yyyy').replace('.', '')
  if (from === null) return `Jusqu'au ${endLabel}`
  const start = DateTime.fromJSDate(from).setLocale('fr')
  // If both dates are the same day, render once.
  if (start.hasSame(end, 'day')) return start.toFormat('d LLL yyyy').replace('.', '')
  return `${start.toFormat('d LLL yyyy').replace('.', '')} - ${endLabel}`
}

const formatRangeSummary = (from: Date | null, to: Date) => {
  const end = DateTime.fromJSDate(to).setLocale('fr')
  const endLabel = end.toFormat('d LLLL yyyy')
  if (from === null) return `Jusqu'au ${endLabel}`
  const start = DateTime.fromJSDate(from).setLocale('fr')
  return `${start.toFormat('d LLLL yyyy')} - ${endLabel}`
}

const WEEKDAY_LABELS = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM']

const rangeCalendarFormatters = {
  formatWeekdayName: (date: Date) => {
    // JS getDay(): 0 = Sunday … 6 = Saturday. Shift so Monday = 0.
    const idx = (date.getDay() + 6) % 7
    return WEEKDAY_LABELS[idx]
  },
  formatCaption: (date: Date) => {
    const label = DateTime.fromJSDate(date).setLocale('fr').toFormat('LLLL yyyy')
    return label.charAt(0).toUpperCase() + label.slice(1)
  },
}

// Entire calendar lays out via flex rows (week) with gap between day cells. The
// range-row's light-green bg is painted on the <tr> via :has() so every cell in
// a row containing any range day sits on top of a single continuous pill —
// matching the Figma exactly, where the row is the visual carrier of the range.
const rangeCalendarClassName = 'p-0'

const rangeCalendarClassNames = {
  // The base Calendar sets the root to w-fit, which collapses the calendar to
  // its intrinsic content width (~230px) and leaves every cell narrow + tall.
  // Force full-width so day cells can spread over their 360px slot.
  root: 'w-full',
  months: 'flex flex-col relative w-full',
  month: 'flex flex-col w-full gap-3',
  // v9 renamed `table` → `month_grid` and `tbody` → `weeks`; without w-full the
  // default table layout shrinks to intrinsic content width.
  month_grid: 'w-full border-collapse',
  weeks: 'w-full',
  nav: 'absolute top-0 inset-x-0 h-9 flex items-center justify-between px-1.5 z-10',
  button_previous: cn(
    'size-6 rounded-full bg-[#deaa4d] hover:bg-[#c9963a] text-white',
    'p-0.5 inline-flex items-center justify-center shadow-sm border-0',
    'disabled:opacity-40 disabled:pointer-events-none',
  ),
  button_next: cn(
    'size-6 rounded-full bg-[#deaa4d] hover:bg-[#c9963a] text-white',
    'p-0.5 inline-flex items-center justify-center shadow-sm border-0',
    'disabled:opacity-40 disabled:pointer-events-none',
  ),
  month_caption: cn(
    'relative flex items-center justify-center h-9 w-full',
    'bg-[#f7f7f7] rounded-2xl',
  ),
  caption_label: 'text-sm font-medium text-[#072c1a] tracking-tight',
  weekdays: 'flex gap-2 mt-1',
  weekday: cn(
    'flex-1 min-w-0 rounded-[10px] py-2',
    'text-[#a3a3a3] text-[0.7rem] font-medium tracking-wider select-none text-center',
  ),
  week: 'flex gap-2 w-full mt-1',
  day: 'flex-1 min-w-0 p-0 text-center group/day select-none',
  // Paint the light-green bg on each range cell and use an after:pseudo to
  // bridge the 8px gap to the next cell. Suppress the bleed at the last column
  // (would extend past the row) and when the range is a single day (the same
  // cell is both range_start and range_end).
  // Range pill visuals. `showOutsideDays={false}` keeps outside cells as
  // invisible placeholders carrying `.rdp-hidden`, so we key the edge-fade on
  // their presence as siblings:
  //   - `:has(+.rdp-hidden)` → last visible cell; fade right to transparent.
  //   - `.rdp-hidden+&`      → first visible cell; fade left to transparent.
  // The fade communicates "range continues into the next/previous month"
  // instead of cutting off abruptly mid-row.
  // The fade variants explicitly force `background-color: transparent`
  // (via `!bg-transparent`) because `bg-gradient-to-*` only sets
  // `background-image` — without the override, the base `bg-primary/10` sits
  // under the gradient and swallows the fade-to-transparent, so visually
  // nothing fades.
  range_start: cn(
    'bg-primary/10 rounded-l-lg relative',
    'after:absolute after:bg-primary/10 after:w-2 after:inset-y-0 after:left-full',
    '[&:nth-child(7)]:after:hidden',
    '[&:has([data-range-end=true])]:after:hidden',
    '[&:has(+.rdp-hidden)]:bg-gradient-to-r [&:has(+.rdp-hidden)]:from-primary/10 [&:has(+.rdp-hidden)]:from-60% [&:has(+.rdp-hidden)]:to-transparent [&:has(+.rdp-hidden)]:!bg-transparent [&:has(+.rdp-hidden)]:rounded-r-none [&:has(+.rdp-hidden)]:after:hidden',
  ),
  range_middle: cn(
    'bg-primary/10 relative',
    'after:absolute after:bg-primary/10 after:w-2 after:inset-y-0 after:left-full',
    '[&:nth-child(7)]:after:hidden',
    '[&:nth-child(1)]:rounded-l-lg',
    '[&:nth-child(7)]:rounded-r-lg',
    '[&:has(+.rdp-hidden)]:bg-gradient-to-r [&:has(+.rdp-hidden)]:from-primary/10 [&:has(+.rdp-hidden)]:from-60% [&:has(+.rdp-hidden)]:to-transparent [&:has(+.rdp-hidden)]:!bg-transparent [&:has(+.rdp-hidden)]:rounded-r-none [&:has(+.rdp-hidden)]:after:hidden',
    '[.rdp-hidden+&]:bg-gradient-to-l [.rdp-hidden+&]:from-primary/10 [.rdp-hidden+&]:from-60% [.rdp-hidden+&]:to-transparent [.rdp-hidden+&]:!bg-transparent [.rdp-hidden+&]:rounded-l-none',
  ),
  range_end: cn(
    'bg-primary/10 rounded-r-lg',
    '[.rdp-hidden+&]:bg-gradient-to-l [.rdp-hidden+&]:from-primary/10 [.rdp-hidden+&]:from-60% [.rdp-hidden+&]:to-transparent [.rdp-hidden+&]:!bg-transparent [.rdp-hidden+&]:rounded-l-none',
  ),
  outside: 'text-[#d1d1d1]',
  today: 'font-semibold',
}

// Reused for the "toujours" unbounded-past modifier: paints every day strictly
// before `to` with the same pill as `range_middle`, including the gap-bridge
// and fade behaviour at month boundaries. The leftmost visible cell of the
// left calendar gets a left-fade (via `.rdp-hidden+&`) to communicate that
// the range continues indefinitely into the past.
const UNBOUNDED_PAST_CLASSES = cn(
  'bg-primary/10 relative',
  'after:absolute after:bg-primary/10 after:w-2 after:inset-y-0 after:left-full',
  '[&:nth-child(7)]:after:hidden',
  '[&:nth-child(1)]:rounded-l-lg',
  '[&:nth-child(7)]:rounded-r-lg',
  '[&:has(+.rdp-hidden)]:bg-gradient-to-r [&:has(+.rdp-hidden)]:from-primary/10 [&:has(+.rdp-hidden)]:from-60% [&:has(+.rdp-hidden)]:to-transparent [&:has(+.rdp-hidden)]:!bg-transparent [&:has(+.rdp-hidden)]:rounded-r-none [&:has(+.rdp-hidden)]:after:hidden',
  '[.rdp-hidden+&]:bg-gradient-to-l [.rdp-hidden+&]:from-primary/10 [.rdp-hidden+&]:from-60% [.rdp-hidden+&]:to-transparent [.rdp-hidden+&]:!bg-transparent [.rdp-hidden+&]:rounded-l-none',
)

function RangeDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const ref = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  const isSelectedSingle =
    modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString()}
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      data-unbounded-past={modifiers.unboundedPast || undefined}
      data-outside={modifiers.outside}
      data-selected-single={isSelectedSingle}
      data-today={modifiers.today || undefined}
      className={cn(
        'relative w-full rounded-lg py-2.5 text-sm font-medium tracking-tight transition-colors',
        'text-[#072c1a]',
        'hover:bg-[#deaa4d] hover:text-[#171717]',
        'focus:outline-none focus-visible:outline-none',
        'data-[outside=true]:text-[#d1d1d1]',
        // Today marker: small amber dot below the number. Always visible so
        // the marker survives range/selection styling (which fills the cell).
        'data-[today=true]:after:absolute data-[today=true]:after:bottom-1 data-[today=true]:after:left-1/2 data-[today=true]:after:-translate-x-1/2 data-[today=true]:after:size-1 data-[today=true]:after:rounded-full data-[today=true]:after:bg-[#deaa4d]',
        'data-[range-middle=true]:text-[#6a9560] data-[range-middle=true]:bg-transparent',
        'data-[unbounded-past=true]:text-[#6a9560] data-[unbounded-past=true]:bg-transparent',
        'data-[range-start=true]:bg-[#6a9560] data-[range-start=true]:text-white data-[range-start=true]:hover:bg-[#6a9560]',
        'data-[range-end=true]:bg-[#6a9560] data-[range-end=true]:text-white data-[range-end=true]:hover:bg-[#6a9560]',
        'data-[selected-single=true]:bg-[#6a9560] data-[selected-single=true]:text-white',
        className,
      )}
      {...props}
    />
  )
}

const rangeCalendarComponents = {
  DayButton: RangeDayButton,
  Chevron: ({ orientation }: { orientation?: 'left' | 'right' | 'up' | 'down' }) =>
    orientation === 'right' ? (
      <ChevronRightIcon className="size-4" strokeWidth={2.5} />
    ) : (
      <ChevronLeftIcon className="size-4" strokeWidth={2.5} />
    ),
}

export function DateRangePicker<TValue extends string = string>({
  value,
  onChange,
  activePreset = null,
  presets,
  className,
  disabled = false,
  placeholder,
  triggerContent,
  maxRangeMonths,
  maxRangeDays,
  minDate,
  maxDate,
  months = 2,
}: DateRangePickerProps<TValue>) {
  const [open, toggleOpen] = useToggle()
  // Staged range — committed only on "Appliquer". Reset to current value
  // every time the popover opens so cancelling truly discards changes.
  // `from: null` ⇒ explicit "toujours" (unbounded past).
  // `from: undefined` ⇒ mid-pick, nothing selected yet.
  const [staged, setStaged] = useState<{ from?: Date | null; to?: Date }>({
    from: value.from,
    to: value.to,
  })
  const [stagedPreset, setStagedPreset] = useState<TValue | null>(activePreset)

  // Each calendar navigates independently — the user explicitly asked for
  // this. Default to [from-month, to-month], or the previous/current month
  // pair when the range collapses to a single calendar (or has no from).
  const initialLeftMonth =
    months === 1
      ? (value.from ?? value.to)
      : (value.from ?? DateTime.fromJSDate(value.to).minus({ months: 1 }).toJSDate())
  const initialRightMonth = (() => {
    if (!value.from) return value.to
    const left = DateTime.fromJSDate(value.from)
    const right = DateTime.fromJSDate(value.to)
    return right.hasSame(left, 'month') ? left.plus({ months: 1 }).toJSDate() : value.to
  })()
  const [leftMonth, setLeftMonth] = useState<Date>(initialLeftMonth)
  const [rightMonth, setRightMonth] = useState<Date>(initialRightMonth)

  // End date sits on the right calendar; start date on the left when the
  // range spans multiple months, otherwise the previous month so both
  // calendars stay populated. Used on open, preset click, and range completion.
  // For unbounded-past ranges, left calendar shows `to - 1 month`.
  const syncMonthsToRange = useCallback(
    (from: Date | null, to: Date) => {
      const t = DateTime.fromJSDate(to)
      setRightMonth(to)
      if (months === 1) {
        setLeftMonth(from ?? to)
        return
      }
      if (from === null) {
        setLeftMonth(t.minus({ months: 1 }).toJSDate())
        return
      }
      const f = DateTime.fromJSDate(from)
      setLeftMonth(t.hasSame(f, 'month') ? t.minus({ months: 1 }).toJSDate() : from)
    },
    [months],
  )

  useEffect(() => {
    if (open) {
      setStaged({ from: value.from, to: value.to })
      setStagedPreset(activePreset)
      syncMonthsToRange(value.from, value.to)
    }
  }, [open, value.from, value.to, activePreset, syncMonthsToRange])

  const handlePresetClick = (preset: DatePreset<TValue>) => {
    const range = preset.getRange()
    setStaged({ from: range.from, to: range.to })
    setStagedPreset(preset.value)
    syncMonthsToRange(range.from, range.to)
  }

  // 3-click cycle derived from `staged`:
  //   - empty or complete range → click sets `from`, clears `to`
  //   - only `from` set        → click sets `to` (swap if before `from`)
  // Uses functional setState so we always read the latest committed range,
  // avoiding stale closures. We intentionally do NOT resync the calendars
  // here — only presets (and initial open) snap months. Manual picking
  // leaves the calendars where the user has navigated them.
  const handleDayClick = (day: Date) => {
    setStagedPreset(null)
    setStaged((prev) => {
      if (!prev.from || prev.to) {
        return { from: day, to: undefined }
      }
      if (day < prev.from) {
        return { from: day, to: prev.from }
      }
      return { from: prev.from, to: day }
    })
  }

  // Plafond de plage : évalué sur la sélection en cours. Mid-pick (`to` absent) compte pour un
  // seul jour ; « toujours » (`from: null`) dépasse par définition.
  const stagedExceedsMax = (() => {
    if (staged.from === undefined) return false
    if (staged.from === null) return maxRangeMonths !== undefined || maxRangeDays !== undefined
    const to = staged.to ?? staged.from
    if (maxRangeMonths !== undefined && rangeExceedsMaxMonths(staged.from, to, maxRangeMonths)) {
      return true
    }
    if (maxRangeDays !== undefined && rangeExceedsMaxDays(staged.from, to, maxRangeDays)) {
      return true
    }
    return false
  })()

  const dayDisabled = (() => {
    if (minDate && maxDate) return [{ before: minDate }, { after: maxDate }]
    if (minDate) return { before: minDate }
    if (maxDate) return { after: maxDate }
    return undefined
  })()

  const handleApply = () => {
    // `undefined` ⇒ mid-pick, nothing to apply. `null` is the "toujours" marker
    // and is explicitly valid — it requires a concrete `to` (always set by
    // the preset, but guard defensively).
    if (staged.from === undefined || stagedExceedsMax) return
    // The picker has no time-of-day UI — days are the unit. Normalize `from`
    // to start-of-day and `to` to end-of-day so a single-day pick covers the
    // full day (otherwise both land at midnight and the range is 0ms wide).
    const endOfDay = (d: Date) => DateTime.fromJSDate(d).endOf('day').toJSDate()
    const startOfDay = (d: Date) => DateTime.fromJSDate(d).startOf('day').toJSDate()
    if (staged.from === null) {
      if (!staged.to) return
      onChange({ from: null, to: endOfDay(staged.to) }, stagedPreset)
      toggleOpen()
      return
    }
    // Single-click selection ⇒ single-day range. The user explicitly asked for
    // this so the picker can be used to inspect a single day without forcing
    // a second click on the same date.
    const to = staged.to ?? staged.from
    onChange({ from: startOfDay(staged.from), to: endOfDay(to) }, stagedPreset)
    toggleOpen()
  }

  const handleCancel = () => {
    setStaged({ from: value.from, to: value.to })
    setStagedPreset(activePreset)
    toggleOpen()
  }

  const triggerLabel = value
    ? formatTriggerLabel(value.from, value.to)
    : (placeholder ?? 'Sélectionner une période')

  // react-day-picker's `selected` can't express "unbounded past" directly.
  // In "toujours" mode we render `to` as a single-day range (so it gets the
  // range-start + range-end data attrs and the solid green pill) and paint
  // every day before it via the `unboundedPast` custom modifier.
  const calendarSelected = ((): { from: Date; to: Date | undefined } | undefined => {
    if (staged.from === null && staged.to) {
      return { from: staged.to, to: staged.to }
    }
    if (staged.from && staged.to) {
      return { from: staged.from, to: staged.to }
    }
    if (staged.from) {
      return { from: staged.from, to: undefined }
    }
    return undefined
  })()

  const calendarModifiers =
    staged.from === null && staged.to ? { unboundedPast: { before: staged.to } } : undefined

  const calendarModifiersClassNames = { unboundedPast: UNBOUNDED_PAST_CLASSES }

  return (
    <Popover open={open} onOpenChange={toggleOpen}>
      <PopoverTrigger
        render={
          <Button variant="outline" disabled={disabled} className={cn('rounded-lg', className)} />
        }
      >
        {triggerContent ? (
          triggerContent({ open, label: triggerLabel, disabled })
        ) : (
          <>
            <CalendarIcon className="size-4 text-muted-foreground" data-icon="inline-start" />
            <span
              className="overflow-hidden text-ellipsis whitespace-nowrap block max-w-full"
              title={triggerLabel}
            >
              {triggerLabel}
            </span>
          </>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 max-w-none overflow-hidden rounded-3xl shadow-2xl bg-[#f4f2ee] border-[#ebebeb]"
        align="start"
        sideOffset={6}
      >
        <div className="flex">
          {/* Preset sidebar */}
          <ul className="flex flex-col gap-2 w-[200px] px-4 pt-5 pb-0.5 bg-[#f4f2ee] border-r border-[#ebebeb]">
            {presets.map((preset) => {
              const isActive = stagedPreset === preset.value
              return (
                <li key={preset.value}>
                  <button
                    type="button"
                    onClick={() => handlePresetClick(preset)}
                    className={cn(
                      'w-full text-left pl-3 pr-2 py-2 text-sm font-medium text-[#072c1a] tracking-tight transition-colors',
                      'focus:outline-none focus-visible:outline-none',
                      isActive ? 'bg-[#6a9560]/50 rounded-2xl' : 'rounded-lg hover:bg-[#6a9560]/10',
                    )}
                  >
                    {preset.label}
                  </button>
                </li>
              )
            })}
          </ul>

          <div className="flex-1 flex flex-col bg-[#f4f2ee]">
            {/* Twin calendars — each navigates independently. They share a single
                range selection so clicking on either commits to the staged range. */}
            <div className="flex border-b border-[#ebebeb]">
              <div
                className={cn(
                  'flex-1 min-w-[360px] p-5',
                  months === 2 && 'border-r border-[#ebebeb]',
                )}
              >
                <Calendar
                  mode="range"
                  numberOfMonths={1}
                  showOutsideDays={false}
                  locale={fr}
                  month={leftMonth}
                  onMonthChange={setLeftMonth}
                  selected={calendarSelected}
                  onSelect={() => {
                    // Selection is driven by onDayClick — ignore RDP's range logic.
                  }}
                  onDayClick={handleDayClick}
                  disabled={dayDisabled}
                  modifiers={calendarModifiers}
                  modifiersClassNames={calendarModifiersClassNames}
                  className={rangeCalendarClassName}
                  classNames={rangeCalendarClassNames}
                  formatters={rangeCalendarFormatters}
                  components={rangeCalendarComponents}
                />
              </div>
              <div className={cn('flex-1 min-w-[360px] p-5', months === 1 && 'hidden')}>
                <Calendar
                  mode="range"
                  numberOfMonths={1}
                  showOutsideDays={false}
                  locale={fr}
                  month={rightMonth}
                  onMonthChange={setRightMonth}
                  selected={calendarSelected}
                  onSelect={() => {
                    // Selection is driven by onDayClick — ignore RDP's range logic.
                  }}
                  onDayClick={handleDayClick}
                  disabled={dayDisabled}
                  modifiers={calendarModifiers}
                  modifiersClassNames={calendarModifiersClassNames}
                  className={rangeCalendarClassName}
                  classNames={rangeCalendarClassNames}
                  formatters={rangeCalendarFormatters}
                  components={rangeCalendarComponents}
                />
              </div>
            </div>

            {/* Footer bar */}
            <div className="flex items-center justify-end gap-4 pl-6 pr-4 py-4 bg-[#f4f2ee]">
              <div className="text-sm mr-auto">
                {(() => {
                  if (staged.from === undefined) {
                    return <span className="text-[#a3a3a3]">Sélectionnez une période</span>
                  }
                  // Single-day preview while mid-pick: mirror `from` as `to` so the
                  // summary always has a concrete end. For "toujours" (from=null)
                  // the preset always supplies a `to`; fall back defensively.
                  const displayTo =
                    staged.to ?? (staged.from instanceof Date ? staged.from : new Date())
                  return (
                    <>
                      <span className="text-[#a3a3a3]">Plage : </span>
                      <span className="font-semibold text-foreground">
                        {formatRangeSummary(staged.from, displayTo)}
                      </span>
                      {stagedExceedsMax ? (
                        <span className="ml-2 font-medium text-[#b3261e]">
                          {maxRangeDays !== undefined
                            ? `Période limitée à ${maxRangeDays} jours`
                            : `Période limitée à ${maxRangeMonths} mois`}
                        </span>
                      ) : null}
                    </>
                  )
                })()}
              </div>
              <button
                type="button"
                onClick={handleCancel}
                className={cn(
                  'w-[88px] px-3 py-2 rounded-full text-sm font-medium text-[#072c1a] tracking-tight',
                  'bg-[#f4f2ee] border border-[#ebebeb] shadow-sm',
                  'hover:bg-white transition-colors',
                  'focus:outline-none focus-visible:outline-none',
                )}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleApply}
                disabled={staged.from === undefined || stagedExceedsMax}
                className={cn(
                  'w-[88px] px-3 py-2 rounded-full text-sm font-medium text-white tracking-tight',
                  'bg-[#6a9560] hover:bg-[#5d8554] transition-colors',
                  'disabled:opacity-50 disabled:pointer-events-none',
                  'focus:outline-none focus-visible:outline-none',
                )}
              >
                Appliquer
              </button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
